import React, { useState, useEffect, useCallback } from 'react';
import { CdsContentPanel } from 'clayco-component-library';
import { CdsDataTable } from 'clayco-component-library/lib';
import { useMsal } from '@azure/msal-react';
import * as Icon from 'react-bootstrap-icons';
import { Modal, Button } from 'react-bootstrap';
import ApiProvider from '../../../apiProvider';
import EmployeeLookup from '../../../Components/EmployeeLookup/EmployeeLookup';
import { isSelfApproval, SELF_APPROVAL_ERROR_MSG } from '../../../utility/selfApprovalCheck';

// HouseholdItemsAdmin: single moveInDate (no start/end range or days calculation) & householditems endpoints.
const HouseholdItemsAdmin = () => {
  const gridColumns = ['Employee', 'Job', 'Submitted', 'Move In Date', 'Approver', 'Reviewed On', 'Is Approved?', 'Actions'];
  const dataKeys = ['employee', 'job', 'submitted', 'moveInDate', 'approver', 'approvedOn', 'isApproved', 'actions'];

  const [submissions, setSubmissions] = useState([]);
  const [allSubmissions, setAllSubmissions] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [filters, setFilters] = useState({ employee: '', from: '', to: '', unapprovedOnly: false });
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Change Approver modal state
  const [changeApproverTarget, setChangeApproverTarget] = useState(null);
  const [newApproverEmployee, setNewApproverEmployee] = useState(null);
  const [changeApproverLoading, setChangeApproverLoading] = useState(false);
  const [changeApproverError, setChangeApproverError] = useState('');

  const { instance: msalInstance, accounts: msalAccounts } = useMsal();
  const activeAccount = msalAccounts?.[0];

  const toMMDDYYYY = (dt) => {
    if (!dt || dt === '0001-01-01T00:00:00') return '';
    const cleaned = String(dt).replace(/"+$/, '');
    const match = cleaned.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, y, m, d] = match;
    return `${m}/${d}/${y}`;
  };
  const formatRow = s => ({
    id: s.id,
    employee: `${s.firstName || ''} ${s.lastName || ''}${s.employeeNumber ? ' (' + s.employeeNumber + ')' : ''}`.trim(),
    job: `${s.projectName || ''}${s.projectNumber ? ' (' + s.projectNumber + ')' : ''}`.trim(),
    submitted: toMMDDYYYY(s.submittedOn) ? '\u200B' + toMMDDYYYY(s.submittedOn) : '',
    moveInDate: toMMDDYYYY(s.moveInDate || s.startDate),
    approver: s.approverName || '',
    approvedOn: toMMDDYYYY(s.approvedOn),
    isApproved: s.isApproved === true ? 'Yes' : s.isApproved === false ? 'No' : '',
    actions: (
      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <button
          type="button"
          title="Change Approver"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          onClick={() => { setNewApproverEmployee(null); setChangeApproverError(''); setChangeApproverTarget(s); }}
        >
          <Icon.PencilFill style={{ color: '#da291c', fontSize: 16, marginRight: '0.25rem' }} />
        </button>
        <button
          type="button"
          title="Delete"
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          onClick={() => handleDelete(s)}
        >
          <Icon.TrashFill style={{ color: '#da291c', fontSize: 15 }} />
        </button>
      </div>
    )
  });



  useEffect(() => {
    if (!hasLoaded && msalInstance && activeAccount) {
      (async () => {
        setLoading(true); setError('');
        try {
          const api = new ApiProvider(msalInstance, activeAccount);
          const response = await api.Get('householditems/all');
          const items = Array.isArray(response?.items) ? response.items : [];
          setAllSubmissions(items);
          setHasLoaded(true);
        } catch {
          setError('Failed to load household items data.');
          setAllSubmissions([]);
          setHasLoaded(true);
        } finally { setLoading(false); }
      })();
    }
  }, [hasLoaded, msalInstance, activeAccount]);



  const applyFilters = useCallback(() => {
    let filtered = allSubmissions.slice();
    if (filters.employee) {
      const emp = filters.employee.toLowerCase();
      filtered = filtered.filter(s => ((s.firstName && s.firstName.toLowerCase().includes(emp)) || (s.lastName && s.lastName.toLowerCase().includes(emp)) || (s.employeeNumber && String(s.employeeNumber).includes(emp))));
    }
    const normalizeSubmissionDate = (val) => { if (!val) return null; const dPart = String(val).slice(0,10); const d = new Date(dPart + 'T00:00:00'); return isNaN(d) ? null : d; };
    const fromDate = filters.from ? new Date(filters.from + 'T00:00:00') : null;
    const toDate = filters.to ? new Date(filters.to + 'T23:59:59') : null;
    if (fromDate) filtered = filtered.filter(s => { const subDate = normalizeSubmissionDate(s.submittedOn); return subDate && subDate.getTime() >= fromDate.getTime(); });
    if (toDate) filtered = filtered.filter(s => { const subDate = normalizeSubmissionDate(s.submittedOn); return subDate && subDate.getTime() <= toDate.getTime(); });
    if (filters.unapprovedOnly) filtered = filtered.filter(s => s.isApproved !== true);
    filtered = filtered.slice().sort((a, b) => (b.submittedOn > a.submittedOn ? 1 : -1));
    setTotalCount(filtered.length);
    setSubmissions(filtered.slice((page - 1) * pageSize, page * pageSize));
  }, [allSubmissions, filters, page, pageSize]);
  useEffect(() => { applyFilters(); }, [applyFilters]);
  const handleFilterChange = (field, value) => { setFilters(prev => ({ ...prev, [field]: value })); setPage(1); };
  const handlePageChange = newPage => setPage(newPage);

  const handleDelete = async (submission) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    if (!msalInstance || !activeAccount) {
      alert('Authentication required.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      await api.Delete(`householditems/admin/${submission.id}`);
      // Remove from cached data
      setAllSubmissions(prev => prev.filter(s => s.id !== submission.id));
      // Filters will be reapplied automatically via useEffect
    } catch (err) {
      setError('Failed to delete submission.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeApproverSave = async () => {
    if (!newApproverEmployee || !changeApproverTarget) return;
    if (!msalInstance || !activeAccount) {
      setChangeApproverError('Authentication required.');
      return;
    }
    // Prevent assigning the submitter as their own approver
    if (isSelfApproval(newApproverEmployee.employeeNumber, changeApproverTarget.employeeNumber)) {
      setChangeApproverError(SELF_APPROVAL_ERROR_MSG);
      return;
    }
    setChangeApproverLoading(true);
    setChangeApproverError('');
    try {
      const api = new ApiProvider(msalInstance, activeAccount);
      await api.Patch(
        `householditems/approver/${changeApproverTarget.id}`,
        { approverEmployeeNumber: newApproverEmployee.employeeNumber, approverName: newApproverEmployee.displayName }
      );
      setAllSubmissions(prev => prev.map(s =>
        s.id === changeApproverTarget.id
          ? { ...s, approverName: newApproverEmployee.displayName, approverId: newApproverEmployee.employeeNumber }
          : s
      ));
      setChangeApproverTarget(null);
      setNewApproverEmployee(null);
    } catch (err) {
      setChangeApproverError('Failed to update approver.');
    } finally {
      setChangeApproverLoading(false);
    }
  };

  async function handleExportCSV() {
    setLoading(true); setError('');
    try {
      if (!msalInstance || !activeAccount) throw new Error('Authentication required.');
      let filtered = allSubmissions.slice();
      if (filters.employee) {
        const emp = filters.employee.toLowerCase();
        filtered = filtered.filter(s => ((s.firstName && s.firstName.toLowerCase().includes(emp)) || (s.lastName && s.lastName.toLowerCase().includes(emp)) || (s.employeeNumber && String(s.employeeNumber).includes(emp))));
      }
      const normalizeSubmissionDate = (val) => { if (!val) return null; const dPart = String(val).slice(0,10); const d = new Date(dPart + 'T00:00:00'); return isNaN(d) ? null : d; };
      const fromDate = filters.from ? new Date(filters.from + 'T00:00:00') : null;
      const toDate = filters.to ? new Date(filters.to + 'T23:59:59') : null;
      if (fromDate) filtered = filtered.filter(s => { const subDate = normalizeSubmissionDate(s.submittedOn); return subDate && subDate.getTime() >= fromDate.getTime(); });
      if (toDate) filtered = filtered.filter(s => { const subDate = normalizeSubmissionDate(s.submittedOn); return subDate && subDate.getTime() <= toDate.getTime(); });
      if (filters.unapprovedOnly) filtered = filtered.filter(s => s.isApproved !== true);
      filtered = filtered.slice().sort((a, b) => (b.submittedOn > a.submittedOn ? 1 : -1));
      const header = ['Employee Number','Employee Name','Job Number','Job Name','Submitted On','Move In Date','Approver Emp Number','Approver Name','Reviewed On','Is Approved?'];
      const rows = filtered.map(s => [
        s.employeeNumber || '',
        `${s.firstName || ''} ${s.lastName || ''}`.trim(),
        s.projectNumber || '',
        s.projectName || '',
        toMMDDYYYY(s.submittedOn),
        toMMDDYYYY(s.moveInDate || s.startDate),
        s.approverId || '',
        s.approverName || '',
        toMMDDYYYY(s.approvedOn),
        s.isApproved === true ? 'Yes' : s.isApproved === false ? 'No' : ''
      ]);
      const csv = [header, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g,'""') + '"').join(',')).join('\r\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const urlObj = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = urlObj; a.download = `HouseholdItemsSubmissions_${new Date().toISOString().slice(0,10)}.csv`; document.body.appendChild(a); a.click();
      setTimeout(() => { document.body.removeChild(a); window.URL.revokeObjectURL(urlObj); }, 500);
    } catch { setError('Failed to export CSV.'); } finally { setLoading(false); }
  }

  const gridItems = submissions.map(formatRow);

  return (
    <>
    <div style={{ padding: '1.5rem' }}>
      <CdsContentPanel
        title={<div style={{display:'flex',justifyContent:'space-between'}}><span>Household Items Submissions (Admin)</span><a href="#" onClick={e => { e.preventDefault(); if (!loading) handleExportCSV(); }} className="text-decoration-underline" style={{ fontWeight: 'normal', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1, textDecoration: 'underline', whiteSpace:'nowrap' }}>{loading && (<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />)}{loading ? 'Exporting…' : 'Export CSV'}</a></div>}
        collapsible={false}
      >
        <div className="d-flex flex-wrap align-items-end mb-3">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <div style={{ minWidth: 320, maxWidth: 400, flex: '1 1 320px' }}>
              <label className="form-label mb-0">Employee (Name or ID)</label>
              <input type="text" className="form-control" value={filters.employee} onChange={e => handleFilterChange('employee', e.target.value)} placeholder="Search by name or Employee ID" />
            </div>
            <div>
              <label className="form-label mb-0">From</label>
              <input type="date" className="form-control" value={filters.from} onChange={e => handleFilterChange('from', e.target.value)} placeholder="YYYY-MM-DD" />
            </div>
            <div>
              <label className="form-label mb-0">To</label>
              <input type="date" className="form-control" value={filters.to} onChange={e => handleFilterChange('to', e.target.value)} placeholder="YYYY-MM-DD" />
            </div>
            <div style={{ minWidth: 160, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div className="form-check form-switch d-flex align-items-center" style={{gap: '0.5rem'}}>
                <input className="form-check-input" type="checkbox" id="unapprovedOnlySwitchHH" checked={filters.unapprovedOnly} onChange={() => setFilters(prev => ({ ...prev, unapprovedOnly: !prev.unapprovedOnly }))} style={{ cursor: 'pointer' }} />
                <span className="ms-2">Unapproved Only</span>
              </div>
            </div>
          </div>
        </div>
        {loading && <div>Loading...</div>}
        {error && <div className="text-danger">{error}</div>}
        <div className="d-flex align-items-center gap-2 mb-2">
          <button className="btn btn-outline-secondary btn-sm" disabled={page === 1} onClick={() => handlePageChange(1)}>First</button>
          <button className="btn btn-outline-secondary btn-sm" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>Prev</button>
          <span>Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}</span>
          <button className="btn btn-outline-secondary btn-sm" disabled={page >= Math.ceil(totalCount / pageSize)} onClick={() => handlePageChange(page + 1)}>Next</button>
          <button className="btn btn-outline-secondary btn-sm" disabled={page >= Math.ceil(totalCount / pageSize)} onClick={() => handlePageChange(Math.ceil(totalCount / pageSize))}>Last</button>
        </div>
        <CdsDataTable columns={gridColumns} dataKeys={dataKeys} data={gridItems} usePaging={false} darkMode={true} pagingControlMode={'none'} />
        <div className="d-flex align-items-center gap-2 mt-2">
          <button className="btn btn-outline-secondary btn-sm" disabled={page === 1} onClick={() => handlePageChange(1)}>First</button>
          <button className="btn btn-outline-secondary btn-sm" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>Prev</button>
          <span>Page {page} of {Math.max(1, Math.ceil(totalCount / pageSize))}</span>
          <button className="btn btn-outline-secondary btn-sm" disabled={page >= Math.ceil(totalCount / pageSize)} onClick={() => handlePageChange(page + 1)}>Next</button>
          <button className="btn btn-outline-secondary btn-sm" disabled={page >= Math.ceil(totalCount / pageSize)} onClick={() => handlePageChange(Math.ceil(totalCount / pageSize))}>Last</button>
        </div>
      </CdsContentPanel>
    </div>

    {/* Change Approver Modal */}
    <Modal
      show={changeApproverTarget !== null}
      onHide={() => { if (!changeApproverLoading) { setChangeApproverTarget(null); setNewApproverEmployee(null); setChangeApproverError(''); } }}
      backdrop="static"
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title>Change Approver</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {changeApproverTarget && (
          <p className="mb-3">
            <strong>Employee:</strong>{' '}
            {`${changeApproverTarget.firstName || ''} ${changeApproverTarget.lastName || ''}`.trim()}
            {changeApproverTarget.employeeNumber ? ` (${changeApproverTarget.employeeNumber})` : ''}
            {changeApproverTarget.approverName ? (
              <><br /><strong>Current Approver:</strong> {changeApproverTarget.approverName}</>
            ) : null}
          </p>
        )}
        <EmployeeLookup
          label="New Approver"
          name="newApprover"
          required
          defaultValue={null}
          onEmployeeSelected={(emp) => setNewApproverEmployee(emp)}
        />
        {changeApproverError && (
          <div className="text-danger mt-2">{changeApproverError}</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={() => { setChangeApproverTarget(null); setNewApproverEmployee(null); setChangeApproverError(''); }}
          disabled={changeApproverLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleChangeApproverSave}
          disabled={!newApproverEmployee || changeApproverLoading}
        >
          {changeApproverLoading ? (
            <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />Saving…</>
          ) : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
    </>
  );
};

export default HouseholdItemsAdmin;
